import { match } from 'ts-pattern';
import { OrderType } from '../types/orders';
import { formatDate, formatDollar, formatNumberWithDecimals } from './formatters';

// ==================== TRADE FORM TOOLTIPS ====================

export const liquidationPriceTooltip = () => {
  return 'If the oracle price drops below the liq. price, your position will be liquidated.';
};

export const orderValueTooltip = (executionPrice: bigint | undefined) => {
  if (!executionPrice) return null;
  return `Estimated execution price: ${formatDollar(executionPrice)}`;
};

export const slippageTooltip = () => {
  return 'Average execution price compared to mid price based on current order book.';
};

export const feesTooltip = () => {
  return 'Taker / Maker fees.';
};

export const reduceOnlyTooltip = () => {
  return 'Reduce or close an existing position. Any order that might increase your position size will be canceled.';
};

export const postOnlyTooltip = () => {
  return 'A Post-Only order will execute only as a maker order. If it would execute as a taker order, it will be automatically canceled.';
};

export const tpSlTooltip = ({
  isEnabled,
  hasProOrders,
}: {
  isEnabled: boolean;
  hasProOrders: boolean;
}) => {
  if (!isEnabled) {
    return 'Coming Soon';
  }

  if (hasProOrders) {
    return 'Places a market TP/SL order. For advanced options like limit prices and partial TP/SL, use the Pro dropdown. TP/SL prices follow oracle prices.';
  }

  return 'Places a market TP/SL order. Triggered by oracle prices.';
};

export const orderTypeTooltip = (orderType: OrderType) =>
  match(orderType)
    .with(
      'MARKET',
      () => 'A Market Order is an order to buy or sell immediately at the current Market Price.'
    )
    .with(
      'LIMIT',
      () =>
        'A Limit Order is an order to buy or sell at a specified price which is triggered once the orderbook moves to this by a taker order.'
    )
    .exhaustive();

export const proOrderSelectTooltip = () => {
  return 'Coming soon';
};

export const crossMarginTooltip = () => {
  return 'Margin is shared across all products.';
};

export const leverageSliderTooltip = () => {
  return 'The leverage slider tool is just a safety feature for fat finger prevention per asset. Real margin control happens through deposits/withdrawals.';
};

// ==================== ACCOUNT SUMMARY ===================

export const accountEquityTooltip = () => {
  return 'Total balance of your account accounting for all uP&L.';
};

export const maintenanceMarginTooltip = () => {
  return 'The minimum account equity required to keep your positions open. If Maintenance Margin = Account Equity your account will be liquidated.';
};

export const marginUsageTooltip = () => {
  return 'Percentage of your total cross margin used by open positions. At 100% margin usage, your account will be liquidated.';
};

export const accountLeverageTooltip = () => {
  return 'Total notional position value / total account equity.';
};

// ==================== MARKET BANNER =====================

export const midPriceTooltip = () => {
  return 'The mid-market price is the simple average of the best bid and the best ask price.';
};

export const oraclePriceTooltip = () => {
  return 'Global spot market price reported by Pyth Lazer, used for liquidations, computing uP&L, and triggering TP/SL orders.';
};

export const openInterestTooltip = () => {
  return 'The total size of all open positions on the contract';
};

export const fundingRateTooltip = () => {
  return 'If funding rate is positive, long position holders pay short position holders, and vice versa if the rate is negative. Funding Fees = Position Value * Funding Rate';
};

export const nextFundingTooltip = () => {
  return 'Time to next funding rate update, calculated hourly.';
};

// ==================== MODAL TOOLTIPS ====================

export const positionStopOrderTooltip = () => {
  return 'A market order will trigger when the set price is reached. By default, take profit and stop loss orders apply to the full position and are automatically cancelled once the position closes.';
};

export const estimatedClosePnlTooltip = (orderType: OrderType) => {
  return `Includes estimated closing fees${orderType === 'LIMIT' ? '.' : ', but does not account for potential slippage.'}`;
};

// ==================== TABLE TOOLTIPS ====================

export const cancelAllOrdersTooltip = (limit: number) => {
  return `Cancel up to ${limit} orders simultaneously`;
};

export const liquidationIconTooltip = () => {
  return 'Account value fell below maintenance margin requirement, triggering forced position closure.';
};

export const positionMaintenanceMarginTooltip = () => {
  return 'The minimum amount of margin required for holding your positions.';
};

export const positionPnlTooltip = () => {
  return 'Oracle price is used to estimate unrealized P&L. The Market Price is used for realized P&L.';
};

export const positionFundingTooltip = () => {
  return 'Net funding payments since the position was opened.';
};

export const feesPreciseTooltip = (feeValue: bigint) => {
  return formatNumberWithDecimals(feeValue);
};

export const pnlClosedRowTooltip = () => {
  return 'The closed P&L includes the total realized P&L, trading fee and funding fee of the position.';
};

// ==================== INPUT TOGGLE TOOLTIPS ====================

export const usdToggleTooltip = () => {
  return 'USD';
};

export const tokenToggleTooltip = (tokenName: string) => {
  return tokenName;
};

// ==================== TIME IN FORCE TOOLTIPS ====================

export const gtdTooltip = () => {
  return "If your order doesn't execute, it will expire in the specified timeframe.";
};

export const iocTooltip = () => {
  return "If your order isn't completely or partially filled immediately, the unfilled portion will be cancelled.";
};

export const fokTooltip = () => {
  return "If your order isn't completely filled immediately, it will be rejected.";
};

// ==================== WITHDRAWAL TOOLTIPS ====================

export const withdrawCountdownTooltip = (lockoutTime: number) => {
  return `Ready at ${formatDate(lockoutTime)}`;
};

// ==================== EXPLORER TOOLTIPS ====================

export const viewInExplorerTooltip = () => {
  return 'View in explorer';
};

export const viewTransactionTooltip = (txHash: string) => {
  return `View transaction ${txHash.substring(0, 6)}...${txHash.substring(txHash.length - 4)} in explorer`;
};

export const addressTooltip = (fullAddress: string) => {
  return fullAddress;
};

// ==================== FEATURE STATUS TOOLTIPS ====================

export const comingSoonTooltip = () => {
  return 'Coming Soon';
};

export const fundingHistoryComingSoonTooltip = () => {
  return 'Coming Soon';
};

// ==================== SIMPLE DISPLAY TOOLTIPS ====================

export const midPriceSimpleTooltip = () => {
  return 'Mid Price';
};

// ==================== ORDER BOOK TOOLTIPS ====================

export const orderbookMidPriceTooltip = () => {
  return 'Mid Price';
};

export const orderbookOraclePriceTooltip = () => {
  return 'Global spot market price reported by Pyth Lazer';
};
